import { Map, fromJS } from 'immutable';
import ComponentRecord from 'Records/ComponentRecord';
import KeyframeRecord from 'Records/KeyframeRecord';
import { batchUndoableActions } from 'Reducers/batchReducer';
import {
  getCurrentAdStateComponents,
  getCurrentAdStateSelectedComponents,
  getSelectedAdstate,
  makeGetComponentsKeyframes,
  makeGetComponentByKey,
  makeSortKeyframes,
  getAd,
  getAdId,
  getAssetIds,
} from 'Selectors';
import { makeGetSelectedComponentsBoundingRect, makeCalculateStyle } from 'Selectors/animation';
import { addNewArtboard } from 'Actions/adStateActions';
import { addAsset } from 'Actions/loadActions';
import { deselectAdState, deselectComponents } from 'Actions/selectionActions';
import componentConfig from '../config/components.json';

export const updateComponentList = (adState, componentList) => ({
  type: 'UPDATE_COMPONENT_LIST',
  adState,
  componentList,
});

export const addComponents = components => ({
  type: 'ADD_COMPONENTS',
  components,
});

export const duplicateComponent = (adState, component, clipboardState, inGroup, assets) => ({
  type: 'DUPLICATE_COMPONENT',
  adState,
  component,
  clipboardState,
  inGroup,
  assets,
});

export const updateComponentActions = (component, actionSetting, skipHistory = false) => ({
  type: 'UPDATE_COMPONENT_ACTIONS',
  component,
  skipHistory,
  actionSetting: new Map(actionSetting),
});

export const updateComponentSettings = (component, setting, skipHistory = false) => ({
  type: 'UPDATE_COMPONENT_SETTINGS',
  component,
  skipHistory,
  setting: new Map(setting),
});

export const updateComponentLoop = (component, loop, skipHistory = false) => ({
  type: 'UPDATE_COMPONENT_LOOP',
  component,
  skipHistory,
  loop,
});

export const toggleLock = component => ({
  type: 'TOGGLE_LOCK_FOR_COMPONENT',
  component,
});

export const toggleVisibility = component => ({
  type: 'TOGGLE_VISIBILITY_FOR_COMPONENT',
  component,
});

export const updateSelectedComponentActions = (action, skipHistory) =>
  (dispatch, getState) => {
    const selectedComponents = getCurrentAdStateSelectedComponents(getState());
    selectedComponents.forEach((component) => {
      dispatch(updateComponentActions(component, action, skipHistory));
    });
  };

export const updateSelectedComponentSettings = (setting, skipHistory) =>
  (dispatch, getState) => {
    const selectedComponents = getCurrentAdStateSelectedComponents(getState());
    selectedComponents.forEach((component) => {
      dispatch(updateComponentSettings(component, setting, skipHistory));
    });
  };

export const toggleLockForSelectedComponents = () =>
  (dispatch, getState) => {
    const selectedComponents = getCurrentAdStateSelectedComponents(getState());
    selectedComponents.forEach((component) => {
      dispatch(toggleLock(component));
    });
  };

export const toggleVisibilityForSelectedComponents = () =>
  (dispatch, getState) => {
    const selectedComponents = getCurrentAdStateSelectedComponents(getState());
    selectedComponents.forEach((component) => {
      dispatch(toggleVisibility(component));
    });
  };

export const addComponent = componentType =>
  (dispatch, getState) => {
    const state = getState();
    const adState = getSelectedAdstate(state);
    if (!adState.selected) return;
    dispatch(deselectComponents());
    dispatch({
      type: 'ADD_NEW_COMPONENT',
      adState,
      componentType,
      componentInfo: componentConfig[componentType],
    }, );
  };

export const deleteComponent = (adState, component) => ({
  type: 'DELETE_COMPONENT',
  adState,
  component,
});

export const createComponentForDroppedAsset = (componentType, style, uploadId) =>
  (dispatch, getState) => {
    const state = getState();
    const adState = getSelectedAdstate(state);
    const settings = Object.assign({}, componentConfig[componentType].settings);
    settings.upload_id = uploadId;
    const css = style;
    if (!adState.selected) return;
    dispatch(deselectComponents());
    dispatch({
      type: 'ADD_NEW_COMPONENT',
      adState,
      componentType,
      componentInfo: {
        settings,
        css
      },
    });
  };

export const uploadDroppedAsset = (file, folderAtDrop) => (dispatch) => {
  const data = {};
  const reader = new FileReader(); // eslint-disable-line no-undef
  const uploadId = Math.random().toString(36).substring(7);
  data.asset = {};
  data.asset.folder_id = folderAtDrop;
  data.asset.file_name = file.name;
  data.asset.upload_id = uploadId;


  reader.addEventListener('load', () => {
    const src = reader.result;
    if (src.includes('data:image')) {
      const img = new Image(); // eslint-disable-line no-undef
      img.onload = () => {
        const style = {
          width: img.width,
          height: img.height
        };
        dispatch(createComponentForDroppedAsset('image', style, uploadId));
      };
      img.src = src;
    } else if (src.includes('data:video')) {
      const video = document.createElement('video'); // eslint-disable-line no-undef
      video.src = src;

      video.addEventListener('loadedmetadata', () => {
        const style = {
          width: video.videoWidth,
          height: video.videoHeight
        };
        dispatch(createComponentForDroppedAsset('video', style, uploadId));
      }, false);
    }

    data.asset.uploaded_file = reader.result;

    fetch('/assets', { // eslint-disable-line no-undef
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name=\'csrf-token\']').content, // eslint-disable-line no-undef
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });
  });

  reader.readAsDataURL(file);
};

export const duplicateComponents = () =>
  (dispatch, getState) => {
    const components = getCurrentAdStateSelectedComponents(getState());
    const adState = getSelectedAdstate(getState());
    dispatch(deselectComponents());
    // note that we only batch actions, and not thunks
    dispatch(
      batchUndoableActions(
        components.map(c => duplicateComponent(adState, c)),
      ),
    );
  };


export const copyComponent = () =>
  (dispatch, getState) => {
    const state = getState();
    const ad = getAd(state);
    const getComponentKeyframes = makeGetComponentsKeyframes();

    const adState = getSelectedAdstate(getState());

    // this only gets top level selected componentList
    const selectedComponents = getCurrentAdStateSelectedComponents(state);
    const adStateComponents = getCurrentAdStateComponents(state);
    const hasSelectedAdSateOnly = selectedComponents.size === 0;

    const components = hasSelectedAdSateOnly ? adStateComponents : selectedComponents;

    const selectedComponentsTree = components.map(c => c.toList(ad)).flatten(true);

    const keyframes = selectedComponentsTree
      .map(component => getComponentKeyframes(state, {
        component
      }))
      .flatten(true);

    const hasSelectedKeyframes = keyframes.filter(k => k.selected).size > 0;

    const clipboardKeyframes = keyframes.filter(k => k.selected).reduce((r, keyframe) => r.set(keyframe.key, keyframe), new Map());

    const clipboardComponents = selectedComponentsTree.reduce((r, component) => r.set(component.key, component), new Map());
    const clipboardAssetIds = selectedComponentsTree.map(component => component.settings.get('assetIds')).flatten(true);
    const clipboardComponentSequence = selectedComponentsTree.map(component => component.key).valueSeq().toArray();

    const adStateMetaData = {
      name: adState.get('name'),
      dimensions: { width: adState.get('viewport').get('width'), height: adState.get('viewport').get('height') },
      backgroundColor: adState.get('backgroundColor'),
      borderColor: adState.get('borderColor'),
      borderWidth: adState.get('borderWidth'),
      script: adState.get('script'),
    };

    const clipBoardData = hasSelectedKeyframes ? new Map({
      componentSequence: clipboardComponentSequence,
      components: clipboardComponents,
      keyframes: clipboardKeyframes,
      selectedKeyframes: true,
    }) : new Map({
      componentSequence: clipboardComponentSequence,
      components: clipboardComponents,
      keyframes,
      assetIds: clipboardAssetIds,
      hasSelectedAdSateOnly,
      adStateMetaData,
    });

    localStorage.setItem('wve-component-clipboard', JSON.stringify(clipBoardData));
  };

const sortComponents = (a, b) => (parseInt(a.key, 10) - parseInt(b.key, 10));

const duplicateAssets = (clipboardAssetIds, state, callback) => {
  const adId = getAdId(state);
  const assetIdsToCloudDuplicate = clipboardAssetIds.filter(clipboardAssetId => !getAssetIds(state).includes(clipboardAssetId));
  return fetch('/assets/duplicate', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
      },
      credentials: 'include',
      body: JSON.stringify({
        assetIds: assetIdsToCloudDuplicate,
        id: adId
      }),
    }).then(response => response.json())
    .then(json => callback(json)).catch(() => null);
};

const pasteKeyframe = (component, style, actions, t) => ({
  type: 'PASTE_KEYFRAME',
  component,
  style,
  actions,
  t,
});

export const pasteComponents = (pasteSpecial = false) =>
  (dispatch, getState) => {
    const state = getState();
    const selectedAdState = getSelectedAdstate(state);
    const { currentTime } = selectedAdState;

    if (!selectedAdState._map.size) return;
    const rawClipboard = localStorage.getItem('wve-component-clipboard');
    const clipboardStateWithoutRecords = fromJS(JSON.parse(rawClipboard));
    const getComponentByKey = makeGetComponentByKey();
    const sortKeyframes = makeSortKeyframes();

    const clipboardState = clipboardStateWithoutRecords.withMutations((map) => {
      map.set('components', map.get('components').map(v => new ComponentRecord(v)));
      map.set('keyframes', map.get('keyframes').map(v => new KeyframeRecord(v)));
    });

    const hasSelectedAdSateOnly = clipboardStateWithoutRecords.get('hasSelectedAdSateOnly');
    const selectedKeyframes = clipboardStateWithoutRecords.get('selectedKeyframes');
    const clipboardKeyframes = clipboardState.get('keyframes');

    document.getElementById('LoadingIndicator').style.display = 'block'; // eslint-disable-line no-undef

    if (selectedKeyframes) {
      const componentSequence = clipboardStateWithoutRecords.get('componentSequence').valueSeq().toArray();
      const calculateStyle = makeCalculateStyle();
      const ad = getAd(state);
      const components = getCurrentAdStateSelectedComponents(state);
      const selectedComponentsTree = components.map(c => c.toList(ad)).flatten(true);
      const indexedSelectedComponents = selectedComponentsTree.map(component => component.key).valueSeq().toArray();
      const componentsList = components.reduce((r, component) => r.set(component.key, component), new Map());
      const clipboardComponents = componentSequence.map(c => clipboardState.get('components').find(cbc => cbc.key === c));
      const clipboardComponentsList = clipboardComponents.reduce((r, component) => r.set(component.key, component), new Map());
      const clipboardComponentsListIndex = clipboardComponentsList.map(c => c.key).valueSeq().toArray();
      const sortedClipboardKeyframes = sortKeyframes(clipboardKeyframes).filter(k => (k.t >= 0));

      dispatch(batchUndoableActions(
        componentsList.map((component) => {
          const componentIndex = (clipboardComponents.length !== 1) ? indexedSelectedComponents.indexOf(component.key) : 0;
          const clipboardComponent = clipboardComponents.find(c => c.key === clipboardComponentsListIndex[componentIndex]);
          const sameComponent = component.key === clipboardComponent.key;
          const keyframes = clipboardComponent ? sortKeyframes(clipboardComponent.get('keyframes')).toArray() : [];
          const clipboardComponentKeyframes = clipboardKeyframes.filter(k => (keyframes.includes(k.key)) && (k.t >= 0));
          const sortedKeyframes = sortKeyframes(clipboardComponentKeyframes);

          const [firstKeyFrame] = sortedClipboardKeyframes.values();
          const currentTimeOffset = firstKeyFrame ? firstKeyFrame.get('t') : 0;

          return sortedKeyframes.map((keyframe) => {
            const keyframeStyle = keyframe.get('style').toJSON();
            const keyframeActions = keyframe.get('actions').toJSON();
            const keyframeTime = currentTime + (keyframe.get('t') - currentTimeOffset);
            const currentStyle = calculateStyle(state, { component, currentTime: keyframeTime });
            if (!sameComponent && !pasteSpecial) {
              keyframeStyle.left = currentStyle.left;
              keyframeStyle.top = currentStyle.top;
              keyframeStyle.width = currentStyle.width;
              keyframeStyle.height = currentStyle.height;
            } else {
              keyframeStyle.width = currentStyle.width;
              keyframeStyle.height = currentStyle.height;
            }
            return dispatch(pasteKeyframe(component, keyframeStyle, keyframeActions, keyframeTime));
          });
        }).toArray()),
      );
      document.getElementById('LoadingIndicator').style.display = 'none'; // eslint-disable-line no-undef
    } else {
      const components = clipboardState.get('components');
      if (hasSelectedAdSateOnly) {
        const adStateMetaData = clipboardStateWithoutRecords.get('adStateMetaData');
        dispatch(deselectAdState());
        const dimensions = { width: adStateMetaData.get('dimensions').get('width'), height: adStateMetaData.get('dimensions').get('height') };
        const style = { backgroundColor: adStateMetaData.get('backgroundColor'), borderColor: adStateMetaData.get('borderColor'), borderWidth: adStateMetaData.get('borderWidth') };
        dispatch(addNewArtboard(adStateMetaData.get('name'), dimensions, style, adStateMetaData.get('script') ));
      }
      const targetAdState = getSelectedAdstate(getState());
      dispatch(deselectComponents());

      return duplicateAssets(clipboardState.get('assetIds'), getState(), (assets) => {
        assets.map(asset => dispatch(addAsset(asset)));
        dispatch(batchUndoableActions(
          components.sort(sortComponents)
            .map((c) => {
              const inGroup = (typeof components.find(cc => cc.containsComponent(c)) !== 'undefined');
              return duplicateComponent(targetAdState, c, clipboardState, inGroup, assets);
            }).toArray()),
        );
        document.getElementById('LoadingIndicator').style.display = 'none'; // eslint-disable-line no-undef
      });
    }
  };


export const pasteSpecialComponents = () => pasteComponents(true);

export const deleteComponents = () => ({
  type: 'DELETE_KEYFRAMES_OR_COMPONENTS',
});

export const sendSelectionToIndex = (newIndex, componentKey) => ({
  type: 'SEND_SELECTION_TO_INDEX',
  newIndex,
  componentKey,
});

export const bringComponentForwards = () => ({
  type: 'SEND_COMPONENTS_FORWARD',
});

export const sendComponentBackwards = () => ({
  type: 'SEND_COMPONENTS_BACKWARD',
});

export const seedUndoHistory = () => ({
  type: 'SEED_HISTORY',
});

export const groupComponents = () => {
  const getSelectedComponentsBoundingRect = makeGetSelectedComponentsBoundingRect();
  return (dispatch, getState) => dispatch({
    type: 'GROUP_COMPONENTS',
    boundingRect: getSelectedComponentsBoundingRect(getState()),
  });
};

export const ungroupComponents = () => {
  const calculateStyle = makeCalculateStyle();
  return (dispatch, getState) => {
    const state = getState();
    const adState = getSelectedAdstate(state);
    const {
      currentTime
    } = adState;

    const selectedComponents = getCurrentAdStateSelectedComponents(getState());
    selectedComponents.filter(c => c.type === 'group').map(group =>
      dispatch({
        type: 'UNGROUP_COMPONENTS',
        groupKey: group.key,
        boundingRect: calculateStyle(state, {
          component: group,
          currentTime
        }),
      }),
    );
  };
};



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Builder/actions/editActions.js